/**
 * AddressVerifier — CB-C NORM-VERIFY
 *
 * Verifica una dirección postal normalizada contra CDAU R2
 * ANTES de invocar CartoCiudad (nivel L9 del orchestrator).
 *
 * Umbral ≥ 80  → retorno directo, skip CartoCiudad
 * Umbral 60-79 → candidato adicional, CartoCiudad actúa igualmente
 * < 60 / error → sin efecto, CartoCiudad sin cambio
 *
 * @module services/geocoding/address
 * @version 1.0.0
 */

import {
  getCDAUR2Geocoder,
  resetCDAUR2Geocoder,
} from '../specialized/CDAUR2Geocoder';

// =============================================================================
// TIPOS
// =============================================================================

/**
 * Resultado de la verificación de dirección contra CDAU R2.
 * Si found=false, todos los campos opcionales son undefined.
 */
export interface AddressVerificationResult {
  found: boolean;
  x?: number;
  y?: number;
  /** Confianza 0-100 (coincide con CDAUR2Result.confidence) */
  confidence?: number;
  /** Dirección normalizada devuelta por CDAU R2 */
  address?: string;
  source: 'cdau_r2' | 'none';
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class AddressVerifier {
  /**
   * Verifica una dirección postal contra CDAU R2.
   *
   * @param address   - Dirección normalizada (ej: "CALLE REAL 5")
   * @param codigoINE - Código INE del municipio (5 dígitos)
   * @returns Resultado de verificación; nunca lanza excepción
   */
  async verify(
    address: string,
    codigoINE: string,
  ): Promise<AddressVerificationResult> {
    // Guardia rápida: sin dirección o INE inválido no se hace fetch
    if (!address || !address.trim()) {
      return { found: false, source: 'none' };
    }
    if (!codigoINE || !/^\d{5}$/.test(codigoINE)) {
      return { found: false, source: 'none' };
    }

    try {
      const geocoder = getCDAUR2Geocoder();
      const result = await geocoder.geocode(address.trim(), codigoINE);

      if (!result) {
        return { found: false, source: 'none' };
      }

      return {
        found: true,
        x: result.x,
        y: result.y,
        confidence: result.confidence,
        address: result.fullAddress,
        source: 'cdau_r2',
      };
    } catch {
      // Degradado gracioso: error de red, CDAU no disponible, etc.
      return { found: false, source: 'none' };
    }
  }
}

// =============================================================================
// SINGLETON
// =============================================================================

let verifierInstance: AddressVerifier | null = null;

/**
 * Devuelve la instancia singleton de AddressVerifier.
 * La caché interna de CDAUR2Geocoder evita fetches repetidos
 * para el mismo municipio dentro de la misma sesión.
 */
export function getAddressVerifier(): AddressVerifier {
  if (!verifierInstance) {
    verifierInstance = new AddressVerifier();
  }
  return verifierInstance;
}

/**
 * Resetea el singleton (útil en tests para aislar estado).
 * También resetea el geocodificador CDAU R2 subyacente.
 */
export function resetAddressVerifier(): void {
  verifierInstance = null;
  resetCDAUR2Geocoder();
}
